import ModalBackButton from "../components/ModalBackButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useState } from "react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { Hex, isHex } from "viem";
import PrimaryButton from "../components/PrimaryButton";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import { formatDisplayWalletAddress } from "../lib/formatDisplayWalletAddress";
import { Chain } from "@paywithglide/glide-js";

type Props = {
  withdrawDestinationChain: Chain;
  onBack: () => void;
  onContinue: (walletAddress: Hex) => void;
};

export default function RecipientWalletAddress({
  withdrawDestinationChain,
  onBack,
  onContinue,
}: Props) {
  const depositConfig = useGlideDepositConfig();
  const { address } = useConnectedWallet();

  const [walletAddress, setWalletAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const showTransferAddress =
    !depositConfig.disableWithdrawToSelfSuggestion &&
    address &&
    isHex(address) &&
    withdrawDestinationChain.id.startsWith("eip155:");

  const isValidAddress = () => {
    if (withdrawDestinationChain.id.includes("solana")) {
      // Solana addresses are base58 encoded, typically 32-44 characters
      return (
        walletAddress.length >= 32 &&
        walletAddress.length <= 44 &&
        /^[1-9A-HJ-NP-Za-km-z]+$/.test(walletAddress)
      );
    }
    // EVM addresses
    return isHex(walletAddress);
  };

  const onClick = async () => {
    if (isValidAddress()) {
      setIsLoading(true);
      onContinue(walletAddress as Hex);
    }
  };

  if (depositConfig.mode !== "withdraw") {
    throw new Error("deposit mode must be withdraw");
  }

  return (
    <>
      <ModalBackButton onClick={() => onBack()} />

      <TransactionCheckoutHeader title="Destination address" />

      <div className="mt-4 mb-2 font-medium text-sm text-[--color-text-primary]">
        {withdrawDestinationChain.name} wallet address
      </div>

      <div className="relative">
        <img
          src={withdrawDestinationChain.logoUrl}
          alt={withdrawDestinationChain.name}
          className="w-5 h-5 rounded-full absolute top-[9px] left-2"
        />

        <input
          className="pl-8 w-full bg-[--color-background-card] text-[--color-text-primary] py-2 px-4 rounded-lg border border-[--color-modal-border] ring-0 outline-none text-sm"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
          placeholder={
            withdrawDestinationChain.id.includes("solana")
              ? "SO1...111"
              : "0x000...000"
          }
        />
      </div>

      {showTransferAddress ? (
        <button
          className="text-xs text-[--color-text-primary] hover:underline underline-offset-4"
          onClick={() => {
            if (isHex(address)) {
              setIsLoading(true);
              onContinue(address);
            }
          }}
        >
          Transfer to {formatDisplayWalletAddress(address)} on{" "}
          {withdrawDestinationChain.name}
        </button>
      ) : null}

      <PrimaryButton
        className="mt-4"
        onClick={() => onClick()}
        loading={isLoading}
        disabled={!isValidAddress()}
      >
        {isValidAddress() ? "Continue" : "Enter a wallet address"}
      </PrimaryButton>
    </>
  );
}
